import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, props } from 'app/pwa/app.cerebral';
import dayjs from 'dayjs';

import LazyImage from 'common/components/LazyImage';

import {
  Wrapper,
  ProfileWrapper,
  Header,
  TextWrapper,
  Title,
  Subtitle,
  TeamWrapper,
} from './elements';
import Spa16 from 'dc/Spa16';
import ChaMeta07 from 'dc/ChaMeta07';
import { useText, useGetAvatarUrl } from 'common/hooks';

type Props = {
  readonly profileKey: string;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly sectionKey: string;
  // eslint-disable-next-line react/no-unused-prop-types
  readonly teamKey: string;
  readonly joinedDatetime: number;
};

const deps = {
  totalParticipantCount: state.challenge.campaign.structure.participantCount,
  teamParticipantCount:
    state.challenge.campaign.structure.sections[props`sectionKey`].teams[
      props`teamKey`
    ].participantCount,
  isLoadingTeamsData: state.challenge.isLoading.teamsData,
  isLoadingChallengeData: state.challenge.isLoading.challengeData,
  teamName:
    state.challenge.campaign.structure.sections[props`sectionKey`].teams[
      props`teamKey`
    ].name,
  profile: state.appContainer.profiles[props`profileKey`],
};

export function UserJoined({
  totalParticipantCount,
  teamParticipantCount,
  isLoadingTeamsData,
  isLoadingChallengeData,
  joinedDatetime,
  profile,
  profileKey,
  teamName,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const getAvatarUrl = useGetAvatarUrl();
  const avatarUrl = getAvatarUrl(profile).small;

  if (isLoadingChallengeData || isLoadingTeamsData) {
    return null;
  }

  return (
    <Wrapper>
      <ProfileWrapper>
        <LazyImage
          key={profileKey}
          onLoading={() => (
            <Header
              icon="icon-group_add"
              isLoadingAvatar
              name={
                profile
                  ? profile.name
                  : t('challenge.latest_events.FeC004.deleted_user')
              }
              time={dayjs(joinedDatetime).fromNow()}
            />
          )}
          onLoaded={imageUrl => (
            <Header
              avatar={imageUrl}
              icon="icon-group_add"
              name={
                profile
                  ? profile.name
                  : t('challenge.latest_events.FeC004.deleted_user')
              }
              time={dayjs(joinedDatetime).fromNow()}
            />
          )}
          path={avatarUrl}
        />
      </ProfileWrapper>
      <TextWrapper>
        <Title>
          {t('challenge.latest_events.FeC004.you_are_participating')}
        </Title>
        <Spa16 />
        <Subtitle>
          {t('challenge.latest_events.FeC004.challenge_participation_subtext', {
            smart_count: totalParticipantCount - 1,
          })}
        </Subtitle>
      </TextWrapper>
      <TeamWrapper>
        <ChaMeta07 title={teamName}>
          {`${teamParticipantCount} (${t(
            'challenge.latest_events.FeC004.your_team'
          )})`}
        </ChaMeta07>
      </TeamWrapper>
    </Wrapper>
  );
}

export default connect<Props, typeof deps>(deps, UserJoined);
